// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  borderRadius,
  deviceSizes,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import { webPath } from 'presentations/routes/path';
import TextField from 'presentations/views/components/atoms/TextField';
import Headline from 'presentations/views/components/atoms/Headline';
import Icon from 'presentations/views/components/atoms/Icon';
import CopyToClipboard from 'react-copy-to-clipboard';
import { blue01, gray06, primary } from 'presentations/views/styles/color';
import Dialog from 'presentations/views/components/atoms/Dialog';
import Transition from 'presentations/views/components/atoms/Transition';
import { useCallback, useMemo, useState } from 'react';
import IconButton from 'presentations/views/components/atoms/IconButton';
import { ListViewType } from 'domains/models/form/listViewType';
import { useRouter } from 'next/router';

interface Props {
  listViewType: ListViewType;
  embedUrl: string | null;
  onClose?: any;
}

export default function Component(props: Props) {
  if (!props.embedUrl) return null;

  const [isCopied, setIsCopied] = useState<boolean>(false);
  const onCopy = useCallback(() => {
    setIsCopied(true);
  }, []);

  const t = translateInComponent(translations);
  const router = useRouter();
  const url = webPath(props.embedUrl, router.locale);
  const width = 320;
  const height = props.listViewType === ListViewType.FullScreen ? 560 : 720;

  const embedCode = useMemo(() => {
    return `<!-- Jicoo widget begin -->
  <div class="jicoo-widget" data-url="${url}" style="min-width:${width}px;height:${height}px;border:1px solid #e4e4e4;box-sizing:content-box;"></div>
  <script type="text/javascript" src="${webPath(
    '/widget/event_type.js',
  )}" async></script>
  <!-- Jicoo widget end -->`;
  }, [router.locale]);

  return (
    <Dialog
      isOpen
      onClose={props.onClose}
      scroll={true}
      width={deviceSizes.desktop}
      titleComponent={<Title>{t('Add to Website')}</Title>}
      actions={
        <CodeCopyButtonContainer>
          {embedCode && (
            <CopyMenuList>
              <CopyMenuContainer>
                <Transition name="collapse" in={isCopied}>
                  <CopiedMenuLink>
                    <Icon name="check" fontSize="small" color="primary" />
                    <CopyMenuLinkTitle>{t('Copied')}</CopyMenuLinkTitle>
                  </CopiedMenuLink>
                </Transition>
                <CopyToClipboard text={embedCode} onCopy={onCopy}>
                  <CopyMenuLink role="button">
                    <Icon
                      name="file_copy_outlined"
                      fontSize="small"
                      color="primary"
                    />
                    <CopyMenuLinkTitle>{t('Copy')}</CopyMenuLinkTitle>
                  </CopyMenuLink>
                </CopyToClipboard>
              </CopyMenuContainer>
            </CopyMenuList>
          )}
        </CodeCopyButtonContainer>
      }
    >
      <CloseButton onClick={props.onClose}>
        <Icon name="close" />
      </CloseButton>
      <Block>
        <Description
          dangerouslySetInnerHTML={{ __html: t('Description', {}, true) }}
        />
        <PreviewTitle variant="h3">{t('Preview')}</PreviewTitle>
        {embedCode && (
          <PreviewContainer width={width} height={height}>
            <iframe src={url} width="100%" height="100%" frameBorder={0} />
          </PreviewContainer>
        )}
        <PreviewTitle variant="h3">{t('Code')}</PreviewTitle>
        <TextField value={embedCode} isFullWidth={true} />
      </Block>
    </Dialog>
  );
}

const Title = styled.div`
  margin: ${spacing(32)} auto 0 auto;
`;
const CloseButton = styled(IconButton)`
  position: absolute;
  right: 16px;
  top: 16px;
`;
const CopyMenuList = styled.div`
  height: 52px;
  border-radius: ${borderRadius()};
  background-color: ${gray06};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  :hover {
    border-radius: ${borderRadius(6)} 0 0 ${borderRadius(6)};
    background-color: ${blue01};
  }
`;
const CopyMenuContainer = styled.div`
  width: 240px;
  height: 52px;
  overflow: hidden;
  border-radius: ${borderRadius(6)} 0 0 ${borderRadius(6)};
  ${mediaMax.tablet} {
    width: 90%;
  }
`;
const Block = styled.div``;
const Description = styled.div`
  text-align: center;
`;
const PreviewTitle = styled(Headline)`
  margin: ${spacing(32)} 0 ${spacing(16)};
  text-align: center;
`;
const PreviewContainer = styled.div<{ width: number; height: number }>`
  padding: ${spacing(24)} ${spacing(24)};
  min-width: ${(props) => {
    return `${props.width}px`;
  }};
  width: auto;
  height: ${(props) => {
    return `${props.height}px`;
  }};
  border: 1px solid #e4e4e4;
`;

const CodeCopyButtonContainer = styled.div`
  margin-top: ${spacing(24)};
  display: flex;
  justify-content: center;
`;

const CopyMenuLink = styled.div`
  height: 52px;
  color: ${primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const CopyMenuLinkTitle = styled.span`
  margin-left: ${spacing(8)};
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};
`;

const CopiedMenuLink = styled.div`
  width: 240px;
  height: 52px;
  color: ${primary};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${blue01};
  :hover {
    border-radius: ${borderRadius(6)} 0 0 ${borderRadius(6)};
  }
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
